import * as THREE from "three";

/*
 * The day: where the sun stands, what colour the sky is, and how dark night gets.
 *
 * Phase 5 decision 8 — **day/night is presentation of sim truth**. The
 * simulation owns the calendar and says only which day it is
 * (`world/types.ts`, `WorldDateView`); where inside that day the world stands is
 * a *projection* over the tick, and this file is what the client does with it.
 * Nothing here reaches the simulation, nothing here is hashed, and no number in
 * this file is the calendar's: a phase is a fraction of whatever a day turns out
 * to be, and the client measures that or asks for it
 * ({@link TimeOfDaySource}) — it never spells it out. The cross-cutting rule is
 * exact about this: *the calendar is spelled once, sim-side.*
 *
 * # Where phase zero is
 *
 * **Dawn.** The day turns at first light, so "the date changed" and "morning
 * came" are the same event — which is what lets the sleep verb stop on the
 * day boundary the client already watches for rather than on a second clock of
 * its own (`main.ts`). It is also the owner's stated reference: a Minecraft day
 * begins at sunrise, and the twenty-minute day is that pacing borrowed whole
 * (`docs/plans/phase-5-plan.md`, decision 7).
 *
 * So: 0 sunrise, 0.25 noon, 0.5 sunset, 0.75 midnight.
 *
 * # One light, pointed two ways
 *
 * The scene has exactly the two lights Gate A left it with — a hemisphere and a
 * directional — and this phase adds none. What moves is where the directional
 * one points and what colour it is: the sun while the sun is up, the moon while
 * it is down. That is a retarget, not a second light, and it matters because
 * the phase's brief is explicit that night gets no new sources (no torches),
 * and because a directional light left pointing up through the floor at
 * midnight would light every overhang from below.
 *
 * # What is judged by looking, and what is not
 *
 * The *arithmetic* is here: the arc is a great circle, the handover at the
 * horizon is a smoothstep, and the palette is a table interpolated between
 * keyframes. **How dark night gets is the owner's taste call** on the art-pass
 * precedent (`docs/plans/gate-a.md`), which is why every colour and every
 * intensity is one named constant in one block below and why `dev/daynight.ts`
 * exists to scrub the whole cycle in a few seconds.
 */

/**
 * Where the world stands in its day, in `[0, 1)` — dawn at zero.
 *
 * **One method, deliberately.** Today the client *measures* the day: the
 * witnessing layer counts stepped seconds between two date changes and divides
 * (`witness/witness.ts`). Wave 3A adds `time_of_day()` to the wasm wall and to
 * `WitnessSource` (plan decision 8, "a small derived seam exposes time-of-day"),
 * and the whole of that change is one implementation swapping for another
 * behind this interface — no consumer moves.
 *
 * `null` means *the client does not know yet*, which is a real answer and not a
 * failure: a session that has not watched a day go by end to end cannot know
 * either the anchor or the length. Callers show fixed morning light for it
 * ({@link MORNING_PHASE}) rather than guessing, because a guess that gets
 * corrected is a sky that jumps.
 */
export interface TimeOfDaySource {
  phase(): number | null;
}

/**
 * The phase a client shows before it knows the time of day.
 *
 * Mid-morning, and the number is chosen so the fallback is *the light this
 * client has always had*: the sun at 0.15 stands about 48 degrees up, which is
 * within a couple of degrees of the fixed sun Gate A's art checkpoint blessed
 * (`render/scene.ts` shipped it at `(0.45, -0.7, 1)`, 50 degrees), and 0.15
 * sits inside the flat daylight band of the palette below, so the colours are
 * that checkpoint's unchanged. A session that never learns the time therefore
 * looks exactly like every session before this phase.
 */
export const MORNING_PHASE = 0.15;

/** Noon, named because the checks and the palette both want to point at it. */
export const NOON_PHASE = 0.25;

/**
 * When the camp verb becomes available, as a phase.
 *
 * A little past sunset rather than at it: at 0.5 the sun is on the horizon and
 * the light is a whole evening's worth, and a verb that let a player skip that
 * would be a verb that skips the best-looking ten percent of the day. By 0.56
 * the dusk ramp below is more than half spent and a player would say it is
 * dark. PROVISIONAL — it is a taste number and it moves with the palette.
 */
export const NIGHT_FROM = 0.56;

/*
 * ---------------------------- The palette ----------------------------
 *
 * Four keyframes and their intensities, and every one of them is a named
 * constant in this one block so that retuning the cycle is a single edit — the
 * brief's requirement, and the shape the owner's screenshot pass needs.
 *
 * Colours are written as sRGB hex, like every other colour in this client:
 * `THREE.Color` converts them to the linear working space on the way in, which
 * is what makes a vertex colour and a light colour comparable (research §3).
 *
 * Intensities are irradiance and the Lambert BRDF divides by pi, so the numbers
 * that look like a lit scene are about pi times the ones that read right
 * written down (`render/scene.ts` says the same thing about the two it shipped).
 */

/**
 * Noon. **These seven are Gate A's numbers, unchanged and on purpose.**
 *
 * The whole daylight look was judged on screenshots at spawn and in a dug pit
 * (`docs/plans/gate-a.md`, "Prototype 0.5"), and a day/night pass that quietly
 * retuned the middle of the day would be throwing that judgement away as a side
 * effect of something else. So the palette is built *outwards* from the frame
 * that was blessed: noon is that frame exactly, and dawn, dusk and night are
 * the new work.
 */
const DAY_ZENITH = 0x4272ae;
const DAY_HORIZON = 0xc3d8ee;
const DAY_SKY_LIGHT = 0x9fc4e8;
const DAY_GROUND_LIGHT = 0x50463a;
const DAY_HEMISPHERE = 2.6;
const DAY_SUN = 0xfff0d6;
const DAY_SUN_INTENSITY = 3.4;

/**
 * Sunrise: a warm horizon under a sky that has not finished being night.
 *
 * The zenith stays deep because that is what a sunrise actually looks like —
 * the warmth is a band at the bottom of the sky and the top of it is still the
 * short end of the spectrum. The hemisphere term is roughly half noon's,
 * because before the sun clears the horizon the sky *is* the light.
 */
const DAWN_ZENITH = 0x2f4f86;
const DAWN_HORIZON = 0xe6a875;
const DAWN_SKY_LIGHT = 0x8ba6cc;
const DAWN_GROUND_LIGHT = 0x463c30;
const DAWN_HEMISPHERE = 1.35;
const DAWN_SUN = 0xffc07a;
const DAWN_SUN_INTENSITY = 3.1;

/**
 * Sunset: the same idea, redder and a little dimmer.
 *
 * Not a mirror of dawn, because the two are not the same picture — evening
 * light is the one people describe with a colour name, so the horizon is pushed
 * further into orange and the ambient a little further down, which is what
 * makes the two ends of the day tell each other apart in a screenshot.
 */
const DUSK_ZENITH = 0x2b3f6e;
const DUSK_HORIZON = 0xdd8a5c;
const DUSK_SKY_LIGHT = 0x7d8ab0;
const DUSK_GROUND_LIGHT = 0x40372d;
const DUSK_HEMISPHERE = 1.15;
const DUSK_SUN = 0xffa863;
const DUSK_SUN_INTENSITY = 2.7;

/**
 * Midnight. **Playable-dark, not black** — the phase's one hard rule about
 * night, and the number the owner is being asked to judge.
 *
 * Where the starting point comes from, since it has to come from somewhere
 * before anybody looks at it: the frame has no tone mapping, so a surface's
 * output is roughly `albedo * irradiance / pi`, and daylight's irradiance works
 * out at about 4.1 in luminance against a mid-albedo terrain — comfortably
 * bright. These two put night at about 0.29, which is seven percent of that:
 * a mid-albedo slope reads at roughly a fifth of full brightness lit and a
 * tenth in the moon's shadow. Dark enough to be night, light enough to walk a
 * terrace down without falling off it.
 *
 * The moon carries most of it rather than the ambient, deliberately: a night
 * lit only by a hemisphere light is a flat night, and shape is what makes dark
 * ground readable at all.
 *
 * The sky is *not* pushed to black either, and for a reason that is about
 * silhouettes rather than taste: the fog takes the horizon colour, so a sky
 * much darker than the ground would draw every ridge as a bright shape against
 * a hole. At these two the horizon and the moonlit ground sit at about the same
 * brightness and the land goes quietly into the dark, which is what night looks
 * like.
 */
const NIGHT_ZENITH = 0x0d1428;
const NIGHT_HORIZON = 0x22304a;
const NIGHT_SKY_LIGHT = 0x33507c;
const NIGHT_GROUND_LIGHT = 0x171820;
const NIGHT_HEMISPHERE = 0.85;
const NIGHT_MOON = 0xbdd0f2;
const NIGHT_MOON_INTENSITY = 0.4;

/**
 * How far the sun's arc leans off vertical, in radians.
 *
 * A sun that passes through the exact zenith is a sun with no shading at noon:
 * every up-facing surface takes the same light and the terraces flatten out.
 * Leaning the great circle about 24 degrees puts the midday sun at 66 degrees
 * and off to one side, which is the same *kind* of light the fixed sun gave and
 * the reason the mesher's ambient-occlusion curve reads in cavities.
 */
const SUN_TILT = 0.42;

/**
 * The band around the horizon the sun and the moon hand over across, as a sine
 * of elevation.
 *
 * The directional light is one light pointed two ways (see the header), so the
 * instant it swaps ends it must be contributing nothing — otherwise every
 * surface in the frame flips its shaded side in one frame. A smoothstep over
 * about six degrees of elevation takes both to zero at the crossing and back up
 * within a percent or two of the day either side.
 */
const HORIZON_BAND = 0.1;

/** Radians in a whole turn. The arc below is the only thing that wants it. */
const TWO_PI = Math.PI * 2;

/** One keyframe of the cycle: everything the frame needs, at one phase. */
interface SkyStop {
  /** Where in the day it stands, in `[0, 1)`. */
  readonly at: number;

  /** The dome straight up, and the dome at the rim — which is also fog and clear. */
  readonly zenith: THREE.Color;
  readonly horizon: THREE.Color;

  /** The hemisphere light's two terms, and how much of it there is. */
  readonly skyLight: THREE.Color;
  readonly groundLight: THREE.Color;
  readonly hemisphere: number;

  /** The one directional light: the sun while it is up, the moon while it is not. */
  readonly sun: THREE.Color;
  readonly sunIntensity: number;
}

/** Builds a keyframe, converting the palette's sRGB hex once at module load. */
function stop(
  at: number,
  zenith: number,
  horizon: number,
  skyLight: number,
  groundLight: number,
  hemisphere: number,
  sun: number,
  sunIntensity: number,
): SkyStop {
  return {
    at,
    zenith: new THREE.Color(zenith),
    horizon: new THREE.Color(horizon),
    skyLight: new THREE.Color(skyLight),
    groundLight: new THREE.Color(groundLight),
    hemisphere,
    sun: new THREE.Color(sun),
    sunIntensity,
  };
}

const DAWN_STOP = stop(
  0,
  DAWN_ZENITH,
  DAWN_HORIZON,
  DAWN_SKY_LIGHT,
  DAWN_GROUND_LIGHT,
  DAWN_HEMISPHERE,
  DAWN_SUN,
  DAWN_SUN_INTENSITY,
);

/**
 * The two ends of full daylight.
 *
 * A pair of identical keyframes rather than one, so the middle of the day is a
 * *plateau* and not a peak: between them nothing interpolates, which is what
 * makes noon reproducible and what keeps {@link MORNING_PHASE} — the fallback a
 * session shows before it knows the time — inside the blessed frame rather than
 * a quarter of the way into a sunrise.
 */
const DAY_OPEN = stop(
  0.12,
  DAY_ZENITH,
  DAY_HORIZON,
  DAY_SKY_LIGHT,
  DAY_GROUND_LIGHT,
  DAY_HEMISPHERE,
  DAY_SUN,
  DAY_SUN_INTENSITY,
);
const DAY_CLOSE = stop(
  0.38,
  DAY_ZENITH,
  DAY_HORIZON,
  DAY_SKY_LIGHT,
  DAY_GROUND_LIGHT,
  DAY_HEMISPHERE,
  DAY_SUN,
  DAY_SUN_INTENSITY,
);

const DUSK_STOP = stop(
  0.5,
  DUSK_ZENITH,
  DUSK_HORIZON,
  DUSK_SKY_LIGHT,
  DUSK_GROUND_LIGHT,
  DUSK_HEMISPHERE,
  DUSK_SUN,
  DUSK_SUN_INTENSITY,
);

/** The two ends of full night, plateaued for the same reason daylight is. */
const NIGHT_OPEN = stop(
  0.62,
  NIGHT_ZENITH,
  NIGHT_HORIZON,
  NIGHT_SKY_LIGHT,
  NIGHT_GROUND_LIGHT,
  NIGHT_HEMISPHERE,
  NIGHT_MOON,
  NIGHT_MOON_INTENSITY,
);
const NIGHT_CLOSE = stop(
  0.9,
  NIGHT_ZENITH,
  NIGHT_HORIZON,
  NIGHT_SKY_LIGHT,
  NIGHT_GROUND_LIGHT,
  NIGHT_HEMISPHERE,
  NIGHT_MOON,
  NIGHT_MOON_INTENSITY,
);

/**
 * The cycle, in order, wrapping from the last stop back to the first.
 *
 * Dawn stands at zero and therefore also at one: the segment after
 * {@link NIGHT_CLOSE} runs back into it, which is what makes the whole thing a
 * loop with no seam to land on.
 */
const STOPS: readonly SkyStop[] = [
  DAWN_STOP,
  DAY_OPEN,
  DAY_CLOSE,
  DUSK_STOP,
  NIGHT_OPEN,
  NIGHT_CLOSE,
];

/**
 * The lighting of one moment.
 *
 * Written into rather than returned fresh: {@link sampleDaylight} is called
 * once a frame and this holds five `THREE.Color`s and a `Vector3`, which is six
 * allocations a frame for a struct whose whole content is overwritten. Build
 * one with {@link makeDaylight} and hand it back in — the same arrangement the
 * scene's scratch quaternions are under (`render/scene.ts`).
 */
export interface Daylight {
  readonly zenith: THREE.Color;
  readonly horizon: THREE.Color;
  readonly skyLight: THREE.Color;
  readonly groundLight: THREE.Color;

  /** The directional light's colour: the sun by day, the moon by night. */
  readonly sun: THREE.Color;

  /**
   * Where that light comes *from*, as a unit vector in the simulation's frame
   * (x/y horizontal, z up). Always above the horizon: below it, this is the
   * moon standing where the sun is not.
   */
  readonly sunDirection: THREE.Vector3;

  hemisphere: number;
  sunIntensity: number;
}

/** A {@link Daylight} to hand to {@link sampleDaylight}. Built once per consumer. */
export function makeDaylight(): Daylight {
  return {
    zenith: new THREE.Color(),
    horizon: new THREE.Color(),
    skyLight: new THREE.Color(),
    groundLight: new THREE.Color(),
    sun: new THREE.Color(),
    sunDirection: new THREE.Vector3(),
    hemisphere: 0,
    sunIntensity: 0,
  };
}

/**
 * Brings any real number into `[0, 1)`.
 *
 * A phase is a position on a loop, so 1.25 is a quarter past dawn and -0.25 is
 * the small hours — wrapping is the correct reading of both, and it is what
 * lets the preview control scrub without clamping at either end. A phase that
 * is not a number at all is somebody's arithmetic having gone wrong upstream,
 * and the honest thing to draw for it is the fallback the client uses when it
 * knows nothing: fixed morning, no strobing.
 */
export function wrapPhase(phase: number): number {
  if (!Number.isFinite(phase)) {
    return MORNING_PHASE;
  }

  return phase - Math.floor(phase);
}

/** Whether it is dark enough that a player would call it night. See {@link NIGHT_FROM}. */
export function isNight(phase: number): boolean {
  return wrapPhase(phase) >= NIGHT_FROM;
}

/**
 * What somebody standing under this sky would call the hour: *first light*,
 * *morning*, *afternoon*, *night*.
 *
 * **Four words off the three thresholds this module already publishes** (chunk
 * Fx-2), and the count is what keeps it honest: the client owns no calendar
 * arithmetic and this is not one — a wall clock would be the renderer inventing
 * hours the simulation does not have, which is a line `dev/daynight.ts` draws
 * explicitly and crosses only for a developer reading a palette.
 *
 * What it is *for* is the dark: a night crossed behind a blackout says the date,
 * and a date does not move until the boundary, so the thing that says the world
 * is turning is this (`main.ts`'s crossing line). It is deliberately coarse —
 * four words over a day is a word every few minutes of play, which is a change
 * somebody notices without it reading as a counter.
 *
 * Phase zero is dawn, so the bands run first light → morning → afternoon →
 * night, and the last of them wraps round to the first.
 */
export function skyWord(phase: number): string {
  const now = wrapPhase(phase);

  if (now >= NIGHT_FROM) {
    return "night";
  }

  if (now >= NOON_PHASE) {
    return "afternoon";
  }

  return now >= MORNING_PHASE ? "morning" : "first light";
}

/**
 * Fills in the lighting of one moment of the day.
 *
 * Two independent things happen here and it is worth saying which is which,
 * because only one of them is a taste call:
 *
 * - **The arc is geometry.** The sun walks a great circle leaned
 *   {@link SUN_TILT} off vertical, rising at phase 0 and setting at 0.5. When
 *   it is under the ground the same vector is reversed and the light is the
 *   moon, which is a retarget rather than a second source (see the header).
 * - **The palette is a table.** The keyframes above, interpolated with a
 *   smoothstep so the ramp has no corner where two segments meet, which is the
 *   difference between a sky that turns and a sky that changes gear.
 *
 * The direct light is then multiplied by how far above the horizon its body
 * actually is ({@link HORIZON_BAND}), which is what makes the handover at
 * sunrise and sunset invisible and what stops a low sun raking the whole map
 * flat at the exact moment it is on the horizon.
 */
export function sampleDaylight(phase: number, into: Daylight): Daylight {
  const wrapped = wrapPhase(phase);
  const angle = TWO_PI * wrapped;

  // The great circle: east at phase 0, leaning towards -y so the midday sun is
  // off to one side rather than overhead. Unit by construction — the two terms
  // are cos and sin of the same angle against orthonormal axes.
  const east = Math.cos(angle);
  const climb = Math.sin(angle);
  const height = climb * Math.cos(SUN_TILT);
  const lean = -climb * Math.sin(SUN_TILT);

  // Whichever body is up. Below the horizon the vector is reversed rather than
  // pointed through the floor: the moon stands where the sun is not.
  const up = height >= 0;
  const side = up ? 1 : -1;

  into.sunDirection.set(east * side, lean * side, height * side);

  const { from, to, t } = segment(wrapped);
  const ramp = smoothstep(t);

  into.zenith.copy(from.zenith).lerp(to.zenith, ramp);
  into.horizon.copy(from.horizon).lerp(to.horizon, ramp);
  into.skyLight.copy(from.skyLight).lerp(to.skyLight, ramp);
  into.groundLight.copy(from.groundLight).lerp(to.groundLight, ramp);
  into.sun.copy(from.sun).lerp(to.sun, ramp);
  into.hemisphere = from.hemisphere + (to.hemisphere - from.hemisphere) * ramp;

  const table = from.sunIntensity + (to.sunIntensity - from.sunIntensity) * ramp;
  into.sunIntensity = table * smoothstep(Math.min(Math.abs(height) / HORIZON_BAND, 1));

  return into;
}

/**
 * Which pair of keyframes a phase falls between, and how far.
 *
 * Written as a walk rather than a binary search because there are six of them,
 * and written to fall off the end into the wrap rather than to index modulo the
 * length: the last segment is the only one whose far end is not in the array,
 * and saying so once here is cheaper than the bounds dance everywhere else.
 */
function segment(phase: number): { from: SkyStop; to: SkyStop; t: number } {
  for (let index = 0; index < STOPS.length; index += 1) {
    const from = STOPS[index];

    if (from === undefined) {
      continue;
    }

    const to = STOPS[index + 1];

    if (to === undefined) {
      // Past the last keyframe: the segment runs back to dawn, which stands at
      // phase one as much as at phase zero.
      return { from, to: DAWN_STOP, t: (phase - from.at) / (1 - from.at) };
    }

    if (phase < to.at) {
      return { from, to, t: (phase - from.at) / (to.at - from.at) };
    }
  }

  // Unreachable while the first keyframe stands at zero, and the honest answer
  // if somebody ever moves it: the day starts at dawn.
  return { from: DAWN_STOP, to: DAWN_STOP, t: 0 };
}

/** Smoothstep, written out: `3t² - 2t³`. The same ramp the sky dome is baked with. */
function smoothstep(t: number): number {
  const clamped = Math.min(Math.max(t, 0), 1);

  return clamped * clamped * (3 - 2 * clamped);
}
